import React, { createContext, useState, useCallback, useRef, ReactNode } from 'react';
import { 
  deviceApi,
  Device, 
  CreateDeviceRequest, 
  // DeviceFilter,
  // DeviceListResponse,
  DeviceDashboardDto,
  CreateDeviceResponse,
  ParameterDtoPreferred,
  DeviceSensorData,
  DeviceDataHeatMap,
  DeviceDataHeatMapMinMax,
  GetDeviceSensorDataRequest,
  UpdateDeviceRequest,
  ExportDeviceDataRequest,
  ExportDeviceDataResponse,
  StatisticalReportRequest,
  AqiInfoData,
  DeviceOffsetsData,
  UpdateDeviceOffsetsDataRequest,
  UpdateDeviceStatusRequest,
  SendWifiCredentialsRequest,
  ExportStatisticalReportDataRequest,
} from '../services/deviceApi';
import { extractDeviceModelNumber } from '../config/deviceModels';
import { FEATURES } from '../config/featureFlags';
import { getUserTimezone } from '../utils/timezoneUtils';

interface DevicesContextType {
  // State
  devices: Device[];
  devicesForDashboard: DeviceDashboardDto[];
  parametersForDashboard: ParameterDtoPreferred[];
  deviceSensorData: DeviceSensorData | null;
  deviceSensorDataForDetailedOverview: DeviceSensorData | null;
  deviceHeatmapData: DeviceDataHeatMap[];
  deviceHeatmapDataMinMax: DeviceDataHeatMapMinMax | null;
  demoDevice: DeviceDashboardDto | null;
  device: Device | null;
  loading: boolean;
  loadingLiveData: boolean;
  error: string | null;
  totalDevices: number;
  currentPage: number;
  totalPages: number;
  statisticalReportData: DeviceSensorData[];
  aqiInfoData: AqiInfoData | null;
  deviceOffsetsData: DeviceOffsetsData[];
  // lastRefreshTime: Date | undefined;
  // Actions
  createDevice: (_deviceData: CreateDeviceRequest) => Promise<CreateDeviceResponse | null>;
  // getDevices: (_filters?: DeviceFilter) => Promise<void>;
  // getDeviceById: (_id: string) => Promise<Device | null>;
  getDevicesForDashboard: () => Promise<DeviceDashboardDto[]>;
  getDemoDevice: () => Promise<DeviceDashboardDto | null>;
  getLiveDataForDashboard: () => Promise<DeviceDashboardDto[]>;
  getParametersForDashboard: () => Promise<ParameterDtoPreferred[]>;
  getDeviceSensorData: (_request: GetDeviceSensorDataRequest) => Promise<DeviceSensorData | null>;
  getDeviceSensorDataForDetailedOverview: (_request: GetDeviceSensorDataRequest) => Promise<DeviceSensorData | null>;
  getDeviceHeatmapData: (_deviceId: number, _fromDate: string, _toDate: string) => Promise<DeviceDataHeatMap[]>;
  updateDevice: (_id: string, _deviceData: UpdateDeviceRequest) => Promise<any | null>;
  updateDeviceStatusBySerialNumber: (_deviceData : UpdateDeviceStatusRequest) => Promise<any | null>;
  getUserDevices: () => Promise<Device[]>;
  deleteDevice: (_id: string) => Promise<any | null>;
  getDeviceHeatmapMinMax: (_deviceId: number, _fromDate: string, _toDate: string) => Promise<DeviceDataHeatMapMinMax | null>;
  // Export functionality
  exportDeviceData: (_deviceId: string, _fileType: string, _serialNumber: string, _deviceName: string) => Promise<ExportDeviceDataResponse | null>;
  exportStatisticalReportData: (_fileType: string) => Promise<ExportDeviceDataResponse | null>;
  exportDashboardData: (_fileType: string) => Promise<ExportDeviceDataResponse | null>;

  // Statistical Report functionality
  getStatisticalReportData: (_request: StatisticalReportRequest) => Promise<DeviceSensorData[]>;
  
  // Device Offsets 
  getDeviceOffsetsData: (_deviceId: string) => Promise<DeviceOffsetsData[]>;
  updateDeviceOffsetsData: (_deviceId: string, _offsetsData: UpdateDeviceOffsetsDataRequest[]) => Promise<boolean>;

  // AQI Info Data functionality
  getAqiInfoData: () => Promise<AqiInfoData>;
  // Utility
  clearError: () => void;
  resetState: () => void;

  //Wifi credentials testing
  SendWifiCredentials: (_wifiData: SendWifiCredentialsRequest) => Promise<any>;

  // Log wifi connection request/response
  logWifiConnection: (_data: {
    deviceId: string;
    timestamp: number;
    request: string;
    response: string;
    attemptNumber: number;
  }) => Promise<any>;

  // Reads the "show demo account" flag from the saved userPreferences.
  // Default true preserves legacy behavior for users who never visited Settings.
  isDemoAccountAllowed: () => boolean;

  // Backend demo device id (from REACT_APP_DEMO_DEVICE_ID). Used by list pages
  // to filter out alerts / share-links tied to the demo device when the user
  // has opted out of demo visibility. NaN if the env var is unset/invalid.
  demoDeviceId: number;
  demoUserId: number;
}

export const DevicesContext = createContext<DevicesContextType | undefined>(undefined);

interface DevicesProviderProps {
  children: ReactNode;
}

export const DevicesProvider: React.FC<DevicesProviderProps> = ({ children }) => {
  // const location = useLocation();
  const userPreferences = localStorage.getItem('userPreferences');
  const userSelectedTimeZone = userPreferences ? JSON.parse(userPreferences).timezone : getUserTimezone();

  // Reads the "show demo account" user preference from localStorage.
  // Re-reads on every call so Settings edits take effect without a remount.
  // Default true preserves existing behavior for users who never visited Settings.
  const isDemoAccountAllowed = useCallback((): boolean => {
    if (!FEATURES.demoDevice) return false; // env master switch — hides demo everywhere
    try {
      const raw = localStorage.getItem('userPreferences');
      if (!raw) return true;
      const p = JSON.parse(raw);
      return p.showDemoAccount !== false;
    } catch {
      return true;
    }
  }, []);
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesForDashboard, setDevicesForDashboard] = useState<DeviceDashboardDto[]>([]);
  const [parametersForDashboard, setParametersForDashboard] = useState<ParameterDtoPreferred[]>([]);
  const [deviceSensorData, setDeviceSensorData] = useState<DeviceSensorData | null>(null);
  const [deviceSensorDataForDetailedOverview, setDeviceSensorDataForDetailedOverview] = useState<DeviceSensorData | null>(null);
  const [deviceHeatmapData, setDeviceHeatmapData] = useState<DeviceDataHeatMap[]>([]);
  const [deviceHeatmapDataMinMax, setDeviceHeatmapDataMinMax] = useState<DeviceDataHeatMapMinMax | null>(null);
  const [exportFromDate, setExportFromDate] = useState<string | null>(null);
  const [exportToDate, setExportToDate] = useState<string | null>(null);
  const [exportReportDeviceId, setExportReportDeviceId] = useState<number | null>(null);
  const [exportSatisticalReportDeviceIds, setExportSatisticalReportDeviceIds] = useState<number[]>([]);
  const [isHeatMap, setIsHeatMap] = useState<boolean>(false);
  // const [lastRefreshTime, setLastRefreshTime] = useState<Date | undefined>(new Date());

  // AQI Info Data
  const [aqiInfoData, setAqiInfoData] = useState<AqiInfoData | null>(null);

  // Statistical Report Data
  const [statisticalReportData, setStatisticalReportData] = useState<DeviceSensorData[]>([]);
  
  // Device offsets data state
  const [deviceOffsetsData, setDeviceOffsetsData] = useState<DeviceOffsetsData[]>([]);

  const [demoDevice, setDemoDevice] = useState<DeviceDashboardDto | null>(null);
  const [device, setDevice] = useState<Device | null>(null);
  // Guard so demo-cleanup runs at most once per app session. It fires the
  // first time we observe the user has 1+ real devices.
  const demoCleanupCalledRef = useRef(false);
  const maybeCleanupDemoArtifacts = useCallback((realDeviceCount: number) => {
    if (demoCleanupCalledRef.current) return;
    if (realDeviceCount <= 0) return;
    demoCleanupCalledRef.current = true;
    deviceApi.deleteDemoDeviceAlertsAndShareLinks().catch(err => {
      // Cleanup is best-effort — log and let the next session retry.
      console.error('Failed to delete demo device alerts/share links:', err);
      demoCleanupCalledRef.current = false;
    });
  }, []);
  const [loading, setLoading] = useState(false);
  const [loadingLiveData, setLoadingLiveData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalDevices, setTotalDevices] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetState = useCallback(() => {
    setDevices([]);
    setDevicesForDashboard([]);
    setParametersForDashboard([]);
    setDeviceSensorData(null);
    setDeviceSensorDataForDetailedOverview(null);
    setDeviceHeatmapData([]);
    setDeviceHeatmapDataMinMax(null);
    setDevice(null);
    setLoading(false);
    setLoadingLiveData(false);
    setError(null);
    setTotalDevices(0);
    setCurrentPage(1);
    setTotalPages(0);
    setExportFromDate(null);
    setExportToDate(null);
    setExportReportDeviceId(null);
    setIsHeatMap(false);
    setStatisticalReportData([]);
  }, []);

  const createDevice = useCallback(async (deviceData: CreateDeviceRequest): Promise<CreateDeviceResponse | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await deviceApi.createDevice(deviceData);
      if (response.success) {
        getDevicesForDashboard();
        getParametersForDashboard();
        // Add the new device to the list
        getUserDevices();
        setTotalDevices(prev => prev + 1);
        return response;
      } else {
        return response;
      }
    } catch (err: any) {
      let errorMessage = 'Failed to create device';
      
      if (err.message) {
        if (err.message.includes('API request failed:')) {
          errorMessage = err.message.replace('API request failed: ', '');
        } else if (err.message.includes('HTTP')) {
          errorMessage = `Server error: ${err.message}`;
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      // Return error response in expected format
      if (err.response && err.response.data) {
        return err.response.data;
      }
      
      // Return error response structure
      return {
        success: false,
        message: errorMessage,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // const getDevices = useCallback(async (filters?: DeviceFilter): Promise<void> => {
  //   try {
  //     setLoading(true);
  //     setError(null);
      
  //     const response: DeviceListResponse = await deviceApi.getDevices(filters);
      
  //     setDevices(response.devices);
  //     setTotalDevices(response.total);
  //     setCurrentPage(response.page);
  //     setTotalPages(response.totalPages);
  //   } catch (err: any) {
  //     const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch devices';
  //     setError(errorMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  // const getDeviceById = useCallback(async (id: string): Promise<Device | null> => {
  //   try {
  //     setLoading(true);
  //     setError(null);
      
  //     const deviceData = await deviceApi.getDeviceById(id);
  //     setDevice(deviceData);
      
  //     return deviceData;
  //   } catch (err: any) {
  //     const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device';
  //     setError(errorMessage);
  //     return null;
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const getDemoDevice = useCallback(async (): Promise<DeviceDashboardDto | null> => {
    try {
      setError(null);
      if (!isDemoAccountAllowed()) {
        setDemoDevice(null);
        return null;
      }
      const device = await deviceApi.getDemoDevice();
      setDemoDevice(device);
      return device;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch demo device';
      setError(errorMessage);
      return null;
    }
  }, []);

  const getDevicesForDashboard = useCallback(async (): Promise<DeviceDashboardDto[]> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real devices and (if preference allows) the demo device in parallel.
      // Demo is shown alongside real devices whenever the preference is on, not
      // only as a zero-device fallback.
      const showDemo = isDemoAccountAllowed();
      const [devices, demo] = await Promise.all([
        deviceApi.getDevicesForDashboard(),
        showDemo ? deviceApi.getDemoDevice().catch(() => null) : Promise.resolve(null),
      ]);

      // Transform Model Number to SEI + Version + Model format
      const transformedDevices = devices.map(device => {
        if (device.serialNumber) {
          const modelNumber = extractDeviceModelNumber(device.serialNumber);
          if (modelNumber) {
            return { ...device, deviceModel: modelNumber };
          }
        }
        return device;
      });

      // Apply the same model number transformation to the demo device
      const transformedDemo = demo
        ? (() => {
            const m = demo.serialNumber ? extractDeviceModelNumber(demo.serialNumber) : null;
            return { ...demo, deviceModel: m || 'DEMO' };
          })()
        : demo;

      // The user-devices endpoint filters by authenticated userId; the demo
      // device has userId=null and is only returned by getDemoDevice, so the
      // two lists never overlap — append directly.
      setDemoDevice(transformedDemo);
      // Only show demo device in the list when the user has no real devices.
      // If requirement changes back to always-show-demo, restore the commented
      // line below and remove the conditional one.
      // setDevicesForDashboard(transformedDemo ? [transformedDemo, ...transformedDevices] : transformedDevices);
      setDevicesForDashboard(
        transformedDemo && transformedDevices.length === 0
          ? [transformedDemo]
          : transformedDevices
      );
      maybeCleanupDemoArtifacts(transformedDevices.length);
      return transformedDevices;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch devices for dashboard';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, [maybeCleanupDemoArtifacts]); // Removed devicesForDashboard from dependencies to prevent stale closures

  const getLiveDataForDashboard = useCallback(async (): Promise<DeviceDashboardDto[]> => {
    try {
      setLoadingLiveData(true);
      setError(null);

      // Fetch real devices and (if preference allows) the demo device in parallel.
      // Demo is shown alongside real devices whenever the preference is on, not
      // only as a zero-device fallback.
      const showDemo = isDemoAccountAllowed();
      const [devices, demo] = await Promise.all([
        deviceApi.getDevicesForDashboard(),
        showDemo ? deviceApi.getDemoDevice().catch(() => null) : Promise.resolve(null),
      ]);

      // Transform Model Number to SEI + Version + Model format
      const transformedDevices = devices.map(device => {
        if (device.serialNumber) {
          const modelNumber = extractDeviceModelNumber(device.serialNumber);
          if (modelNumber) {
            return { ...device, deviceModel: modelNumber };
          }
        }
        return device;
      });

      // Apply the same model number transformation to the demo device
      const transformedDemo = demo
        ? (() => {
            const m = demo.serialNumber ? extractDeviceModelNumber(demo.serialNumber) : null;
            return { ...demo, deviceModel: m || 'DEMO' };
          })()
        : demo;

      // The user-devices endpoint filters by authenticated userId; the demo
      // device has userId=null and is only returned by getDemoDevice, so the
      // two lists never overlap — append directly.
      setDemoDevice(transformedDemo);
      // Only show demo device in the list when the user has no real devices.
      // If requirement changes back to always-show-demo, restore the commented
      // line below and remove the conditional one.
      // setDevicesForDashboard(transformedDemo ? [transformedDemo, ...transformedDevices] : transformedDevices);
      setDevicesForDashboard(
        transformedDemo && transformedDevices.length === 0
          ? [transformedDemo]
          : transformedDevices
      );
      maybeCleanupDemoArtifacts(transformedDevices.length);
      return transformedDevices;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch devices for dashboard';
      setError(errorMessage);
      return [];
    } finally {
      setLoadingLiveData(false);
    }
  }, [maybeCleanupDemoArtifacts]); // Removed devicesForDashboard from dependencies to prevent infinite loops

  const getParametersForDashboard = useCallback(async (): Promise<ParameterDtoPreferred[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const parameters = await deviceApi.getParametersForDashboard();
      setParametersForDashboard(parameters);
      return parameters;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch parameters for dashboard';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserDevices = useCallback(async (): Promise<Device[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const devices = await deviceApi.getUserDevices();
      setDevices(devices);
      setTotalDevices(devices.length);
      return devices;
    }
    catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch user devices';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getDeviceSensorData = useCallback(async (request: GetDeviceSensorDataRequest): Promise<DeviceSensorData | null> => {
    try {
      setLoading(true);
      setError(null);
      setIsHeatMap(false);
      setDeviceSensorData(null);
      setExportFromDate(request.FromDate);
      setExportToDate(request.ToDate);
      setExportReportDeviceId(request.DeviceId);

      const sensorData = await deviceApi.getDeviceSensorData(request);
      setDeviceSensorData(sensorData);
      return sensorData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDeviceSensorDataForDetailedOverview = useCallback(async (request: GetDeviceSensorDataRequest): Promise<DeviceSensorData | null> => {
    try {
      setLoading(true);
      setError(null);
      setIsHeatMap(false);
      setExportFromDate(request.FromDate);
      setExportToDate(request.ToDate);
      
      const sensorData = await deviceApi.getDeviceSensorDataForDetailedOverview(request);
      setDeviceSensorDataForDetailedOverview(sensorData);
      return sensorData;
    }
    catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get statistical report data
  const getStatisticalReportData = useCallback(async (request: StatisticalReportRequest): Promise<DeviceSensorData[]> => {
    try {
      setLoading(true);
      setError(null);
      setExportFromDate(request.fromDate);
      setExportToDate(request.toDate);
      setExportSatisticalReportDeviceIds(request.deviceIds);
      const sensorData = await deviceApi.getStatisticalReportData(request);
      setStatisticalReportData(sensorData);
      return sensorData;
    }catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch statistical report data';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getDeviceHeatmapData = useCallback(async (deviceId: number,
    fromDate: string,
    toDate: string): Promise<DeviceDataHeatMap[]> => {
    try {
      setLoading(true);
      setError(null);
      setIsHeatMap(true);
      setExportFromDate(fromDate);
      setExportToDate(toDate);

      
      const sensorData = await deviceApi.getDeviceHeatmapData(deviceId, fromDate, toDate);
      setDeviceHeatmapData(sensorData);
      return sensorData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const updateDevice = useCallback(async (id: string, deviceData: UpdateDeviceRequest): Promise<any | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await deviceApi.updateDevice(id, deviceData);
      if(response.success) {
        await getUserDevices();
        await getDevicesForDashboard();
        // Add the new device to the list
        return response;
      } else {
        return response;
      }
      // setDevice(response);
    }
    catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update device';
      setError(errorMessage);
      return err.response?.data || { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUserDevices, getDevicesForDashboard]);

  //Update device status by serial number
  const updateDeviceStatusBySerialNumber = useCallback(async (deviceData: UpdateDeviceStatusRequest): Promise<any | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await deviceApi.updateDeviceStatusBySerialNumber(deviceData);
      if(response.success) {
        await getUserDevices();
        await getDevicesForDashboard();
        // Add the new device to the list
        return response;
      } else {
        return response;
      }

    }
    catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update device';
      setError(errorMessage);
      return err.response?.data || { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [getUserDevices, getDevicesForDashboard]);

  const deleteDevice = useCallback(async (id: string): Promise<any | null> => {
    try {
      // setLoading(true);
      setError(null);
      const response = await deviceApi.deleteDevice(id);
      if(response.success) {
        // Refresh device lists after successful deletion
        setTimeout(() => {          
          getDevicesForDashboard();
          getUserDevices();
        }, 1000);
        return response;
      } else {
        return response;
      }
    }
    catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete device';
      setError(errorMessage);
      return err.response.data;
    } finally {
      // setLoading(false);
    }
  }, [getDevicesForDashboard, getUserDevices]);

  const getDeviceHeatmapMinMax = useCallback(async (deviceId: number,
    fromDate: string,
    toDate: string): Promise<DeviceDataHeatMapMinMax | null> => {
    try {
      setLoading(true);
      setError(null);
      setIsHeatMap(true);
      setExportFromDate(fromDate);
      setExportToDate(toDate);
      
      const sensorData = await deviceApi.getDeviceHeatmapDataMinMax(deviceId, fromDate, toDate);
      setDeviceHeatmapDataMinMax(sensorData);
      return sensorData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Export device data - main export function
  const exportDeviceData = useCallback(async (deviceId: string, fileType: string, serialNumber: string, deviceName: string): Promise<ExportDeviceDataResponse | null> => {
    try {

      console.log('Export device data:', { deviceId, fileType, exportFromDate, exportToDate, serialNumber, deviceName });

      // if(deviceSensorData === null || deviceSensorData.readings.length <= 0) {
      //  alert('No data found for the selected date range');
      //   return null;
      // }

      if (!exportFromDate || !exportToDate) {
        console.error('From date and to date are required');
        return null;
      }

      if (deviceId === '') {
        const deviceDetails = devicesForDashboard.find(device => device.deviceId === exportReportDeviceId);
        if (deviceDetails) {
          deviceId = deviceDetails.deviceId.toString();
          serialNumber = deviceDetails.serialNumber || '';
          deviceName = deviceDetails.deviceName || '';
        }
      }  
      // For Excel/CSV - use backend API
      const request: ExportDeviceDataRequest = {
        deviceId: parseInt(deviceId),
        fileType,
        fromDate: exportFromDate,
        toDate: exportToDate,
        serialNumber,
        deviceName,
        isHeatMap: isHeatMap,
        userTimeZoneId : userSelectedTimeZone,
      };

      let response: ExportDeviceDataResponse | null = null;

      if (fileType === 'pdf') {
        response = await deviceApi.exportToPDF(request);
      }
      else if (fileType === 'png') {
        response = await deviceApi.exportToImage(request);
      }
      else {
        response = await deviceApi.exportDeviceData(request);
      }


      
      if (response && response.blob && response.fileName) {
        // Auto-download the file
        await deviceApi.downloadFile(response.blob, response.fileName);
        return response;
      } else {
        throw new Error('No file received from server');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      console.error('Export error:', errorMessage);
      return null;
    } 
  }, [exportFromDate, exportToDate, exportReportDeviceId]);

  // Export device data - main export function
  const exportStatisticalReportData = useCallback(async (fileType: string): Promise<ExportDeviceDataResponse | null> => {
    try {

      if (!exportFromDate || !exportToDate) {
        console.error('From date and to date are required');
        return null;
      }

      let response: ExportDeviceDataResponse | null = null;
      const request: ExportStatisticalReportDataRequest = {
        deviceIds: exportSatisticalReportDeviceIds,
        fromDate: exportFromDate,
        toDate: exportToDate,
        fileType,
        userTimeZoneId: userSelectedTimeZone,
      }

      response = await deviceApi.exportStatisticalReportData(request);
      
      if (response && response.blob && response.fileName) {
        // Auto-download the file
        await deviceApi.downloadFile(response.blob, response.fileName);
        return response;
      } else {
        throw new Error('No file received from server');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      console.error('Export error:', errorMessage);
      return null;
    } 
  }, [exportFromDate, exportToDate, exportSatisticalReportDeviceIds]);

  const exportDashboardData = useCallback(async (fileType: string): Promise<ExportDeviceDataResponse | null> => {
    try {

      if (!fileType) {
        console.error('File type is required');
        return null;
      }

      const userTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const request: ExportDeviceDataRequest = {
        fileType,
        deviceId: null,
        fromDate: null,
        toDate: null,
        serialNumber: null,
        deviceName: 'All Devices',
        isHeatMap: isHeatMap,
        userTimeZoneId : userTimeZoneId,
      };

      let response: ExportDeviceDataResponse | null = null;

      if (fileType === 'pdf') {
        response = await deviceApi.exportToPDF(request);
      }
      else if (fileType === 'png') {
        response = await deviceApi.exportToImage(request);
      }
      else {
        response = await deviceApi.exportDashboardData(fileType, userTimeZoneId);
      }

      if (response && response.blob && response.fileName) {
        await deviceApi.downloadFile(response.blob, response.fileName);
        return response;
      } else {
        throw new Error('No file received from server');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      console.error('Export error:', errorMessage);
      return null;
    }
  }, []);

  const getAqiInfoData = useCallback(async (): Promise<AqiInfoData> => {
    try {
      const data = await deviceApi.getAqiInfoData();
      setAqiInfoData(data);
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch AQI info data';
      console.error('AQI info data error:', errorMessage);
      return {
        aqiParameters: [],
        aqiCategoryData: [],
        nonAqiParameters: [],
        nonAqiCategoryData: [],
        healthParameters: [],
        healthCategoryData: [],
      };
    }
  }, []);

  // Device offsets data 
  const getDeviceOffsetsData = useCallback(async (deviceId: string): Promise<DeviceOffsetsData[]> => {
    try {
      setError(null);

      const deviceOffsetsData = await deviceApi.getDeviceOffsetsById(deviceId);
      setDeviceOffsetsData(deviceOffsetsData);
      return deviceOffsetsData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return [];
    }
  }, []);

    // Device offsets data 
    const updateDeviceOffsetsData = useCallback(async (deviceId: string, requestData: UpdateDeviceOffsetsDataRequest[]): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);
  
        const response = await deviceApi.updateDeviceOffsetsData(deviceId, requestData);
        if(response){
          getDeviceOffsetsData(deviceId);
        }
        return response;
        
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    }, []);

    //send wifi cedentials for testing
  const SendWifiCredentials = useCallback(async (requestData: SendWifiCredentialsRequest): Promise<any> => {
    try {
      const response = await deviceApi.SendWifiCredentials_old(requestData);
      return response;

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch device sensor data';
      setError(errorMessage);
      return false;
    }
  }, []);

  // Log wifi connection request/response to backend
  const logWifiConnection = useCallback(async (data: {
    deviceId: string;
    timestamp: number;
    request: string;
    response: string;
    attemptNumber: number;
  }): Promise<any> => {
    try {
      const response = await deviceApi.logWifiConnection(data);
      return response;
    } catch (err: any) {
      console.error('Failed to log wifi connection:', err);
      return { success: false };
    }
  }, []);

  const contextValue: DevicesContextType = {
    // State
    devices,
    devicesForDashboard,
    parametersForDashboard,
    deviceSensorData,
    deviceSensorDataForDetailedOverview,
    deviceHeatmapData,
    deviceHeatmapDataMinMax,
    demoDevice,
    device,
    loading,
    loadingLiveData,
    error,
    totalDevices,
    currentPage,
    totalPages,
    // AQI Info Data
    aqiInfoData,
    // Statistical Report functionality
    statisticalReportData,
    // Device offsets data
    deviceOffsetsData,


    // Actions
    createDevice,
    getDevicesForDashboard,
    getDemoDevice,
    getLiveDataForDashboard,
    getParametersForDashboard,
    getDeviceSensorData,
    getDeviceSensorDataForDetailedOverview,
    getDeviceHeatmapData,
    updateDevice,
    updateDeviceStatusBySerialNumber,
    getUserDevices,
    deleteDevice,
    getDeviceHeatmapMinMax,
    getStatisticalReportData,
    // Export functionality
    exportDeviceData,
    exportStatisticalReportData,
    exportDashboardData,
    getAqiInfoData,
    // Device offsets
    getDeviceOffsetsData,
    updateDeviceOffsetsData,
    // Utility
    clearError,
    resetState,
    // Wifi testing
    SendWifiCredentials,
    logWifiConnection,
    // Demo account preference
    isDemoAccountAllowed,
    demoDeviceId: Number(process.env.REACT_APP_DEMO_DEVICE_ID),
    demoUserId: Number(process.env.REACT_APP_DEMO_USER_ID)
  };

  return (
    <DevicesContext.Provider value={contextValue}>
      {children}
    </DevicesContext.Provider>
  );
};
